import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";
import { CTAButton } from "@/components/CTAButton";
import TrustBar from "@/components/TrustBar";
import { IMAGES, SERVICE_AREAS } from "@/data/content";

const VALUES = [
  { n: "01", title: "Craftsmanship", body: "Every surface is applied by hand, by specialists who treat finishing as a craft — not a trade." },
  { n: "02", title: "Precision", body: "Millimetre tolerances, floated planes and engineered detailing. Precision is our baseline, not our ambition." },
  { n: "03", title: "Quality", body: "Quality systems, certified application and a workmanship guarantee behind every project we complete." },
];

export default function About() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 100]);

  return (
    <div>
      <section className="relative h-[80vh] min-h-[520px] overflow-hidden" data-testid="about-hero">
        <motion.img style={{ y }} src={IMAGES.interiorLight} alt="CEMTEX studio" className="absolute inset-0 w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
        <div className="relative h-full container-x flex flex-col justify-end pb-16 md:pb-24 text-white">
          <Overline light className="mb-6">About CEMTEX</Overline>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[6rem] font-extrabold leading-[0.9] tracking-tighter uppercase">A Studio,<br />Not a Trade.</h1>
        </div>
      </section>

      <section className="container-x py-24 md:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal><Overline className="mb-6">Our Story</Overline></Reveal>
        </div>
        <div className="lg:col-span-7 space-y-6">
          <Reveal>
            <p className="font-display text-2xl md:text-3xl font-bold leading-snug">CEMTEX exists to bring architectural surface finishes to Western Australia with the discipline of a studio and the reliability of a specialist.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-lg font-light text-taupe leading-relaxed">We founded CEMTEX because great surfaces deserve exacting execution. From seamless microcement and hand-burnished Venetian plaster to certified waterproofing and structural remediation, we approach every project as a piece of architecture — considered, precise and built to endure.</p>
            <p className="text-lg font-light text-taupe leading-relaxed mt-6">Our clients are homeowners, interior designers, architects and commercial developers who expect an exceptional result. We deliver it across Perth Metro and Regional WA, backed by full licensing, insurance and a workmanship guarantee.</p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="about-values">
        <div className="container-x">
          <Overline className="mb-14">What We Stand For</Overline>
          <div className="grid md:grid-cols-3 gap-px bg-black/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.1}>
                <div className="bg-offwhite h-full p-8 md:p-10">
                  <span className="font-mono text-sm text-sand">{v.n}</span>
                  <h3 className="font-display text-3xl font-extrabold mt-4 mb-3">{v.title}</h3>
                  <p className="text-taupe font-light leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials + coverage */}
      <section className="container-x py-24 md:py-32 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <div>
            <Overline className="mb-6">Credentials</Overline>
            <ul className="space-y-4">
              {["Fully licensed & insured", "Certified waterproofing applicators", "Residential & commercial experience", "Workmanship guarantee on every project", "Quality, manufacturer-approved systems"].map((c) => (
                <li key={c} className="flex items-center gap-4 border-b border-black/10 pb-4 text-lg font-light">
                  <span className="h-2 w-2 bg-sand shrink-0" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div>
            <Overline className="mb-6">Service Area Coverage</Overline>
            <p className="text-lg font-light text-taupe leading-relaxed mb-6">Delivering across Perth Metro and Regional Western Australia, including:</p>
            <div className="flex flex-wrap gap-2">
              {SERVICE_AREAS.map((a) => (
                <span key={a} className="border border-black/15 px-4 py-2 text-xs uppercase tracking-[0.15em] text-charcoal">{a}</span>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <TrustBar dark />

      <section className="relative py-32 md:py-40 overflow-hidden text-center" data-testid="about-cta">
        <img src={IMAGES.plasterTexture} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/80" />
        <div className="relative container-x text-white">
          <h2 className="font-display text-4xl md:text-6xl font-extrabold mb-10">Let's create something enduring.</h2>
          <CTAButton to="/contact" testid="about-cta-button" variant="accent">Get a Free Quote</CTAButton>
        </div>
      </section>
    </div>
  );
}
